import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import type { UnitCategory } from "@/types";

// Import all SVG icons at build time
const iconModules = import.meta.glob<{ default: string }>(
    "@/assets/icons/*.svg",
    { eager: true },
);

// Create a map of dimension keys to their bundled URLs
const iconMap: Record<string, string> = {};
for (const path in iconModules) {
    const filename = path.split("/").pop()?.replace(".svg", "");
    if (filename && iconModules[path]) {
        iconMap[filename] = iconModules[path].default;
    }
}

interface UnitImageProps {
    length: number;
    width: number;
    category: UnitCategory | null;
    className?: string;
}

export const UnitImage = ({
    length,
    width,
    className,
    category,
}: UnitImageProps) => {
    const [imageError, setImageError] = React.useState(false);
    const [imageLoaded, setImageLoaded] = React.useState(false);

    // Determine image source: use category image if available, otherwise look up from bundled icons
    const dimensionKey = `${width}x${length}`;
    const imageSrc = category?.image || iconMap[dimensionKey];

    const handleImageLoad = () => {
        setImageLoaded(true);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageError(true);
        setImageLoaded(false);
        console.warn(`Failed to load image: ${imageSrc}`);
    };

    if (imageError || !imageSrc) {
        // Show skeleton if image fails to load or no image available
        return <Skeleton className="tw:h-[75px] tw:w-[75px]" />;
    }

    return (
        <>
            {!imageLoaded && <Skeleton className="tw:h-[75px] tw:w-[75px]" />}
            <img
                src={imageSrc}
                alt={`${width}x${length} storage unit`}
                className={className}
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ display: imageLoaded ? "block" : "none" }}
            />
        </>
    );
};
